import { Form, Input, Button, Row, Col } from "antd";
import { ToastService } from "@shared/lib/services";
import { IBrandBilling } from "@shared/lib/types/brandBilling";
import { InputNumber } from "@shared/ui/InputNumber";

interface BrandBillingFormProps {
  onSubmit: (values: Partial<IBrandBilling>) => void;
  isSubmitting: boolean;
}

export function BrandBillingForm({
  onSubmit,
  isSubmitting,
}: BrandBillingFormProps) {
  const [form] = Form.useForm();

  const handleSubmit = (values: any) => {
    const {
      addressLine1,
      addressLine2,
      city,
      state,
      country,
      pinCode,
      ...rest
    } = values;

    const formattedValues = {
      ...rest,
      address: {
        addressLine1,
        addressLine2,
        city,
        state,
        country,
        pinCode,
      },
    };

    onSubmit(formattedValues);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        gstRate: 0,
        serviceChargeRate: 0,
      }}
      onFinishFailed={({ errorFields }) => {
        if (errorFields.length) {
          ToastService.error(
            "Error Submitting Form. Please fill all required fields."
          );
        }
      }}
    >
      <div style={{ height: "600px", overflowY: "scroll" }}>
        <Form.Item
          name="name"
          label="Billing Name"
          rules={[
            { required: true, message: "Billing name is required" },
            { min: 3, message: "Billing name must be at least 3 characters" },
          ]}
        >
          <Input placeholder="Enter billing name" />
        </Form.Item>

        <Form.Item
          name="gst"
          label="GST Number"
          rules={[
            { required: true, message: "GST number is required" },
            // {
            //   pattern:
            //     /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
            //   message: "Please enter a valid GST number",
            // },
          ]}
        >
          <Input placeholder="Enter GST number" />
        </Form.Item>

        <Form.Item
          name="addressLine1"
          label="Address Line 1"
          rules={[
            { required: true, message: "Address line 1 is required" },
            { min: 5, message: "Address line 1 must be at least 5 characters" },
          ]}
        >
          <Input.TextArea placeholder="Enter address line 1" rows={2} />
        </Form.Item>

        <Form.Item
          name="addressLine2"
          label="Address Line 2"
          rules={[
            { required: true, message: "Address line 2 is required" },
            { min: 5, message: "Address line 2 must be at least 5 characters" },
          ]}
        >
          <Input.TextArea placeholder="Enter address line 2" rows={2} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="city"
              label="City"
              rules={[
                { required: true, message: "City is required" },
                { min: 2, message: "City must be at least 2 characters" },
              ]}
            >
              <Input placeholder="Enter city" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="state"
              label="State"
              rules={[
                { required: true, message: "State is required" },
                { min: 2, message: "State must be at least 2 characters" },
              ]}
            >
              <Input placeholder="Enter state" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="country"
              label="Country"
              rules={[
                { required: true, message: "Country is required" },
                { min: 2, message: "Country must be at least 2 characters" },
              ]}
            >
              <Input placeholder="Enter country" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="pinCode"
              label="PIN Code"
              rules={[
                { required: true, message: "PIN code is required" },
                {
                  pattern: /^[0-9]{6}$/,
                  message: "Please enter a valid 6-digit PIN code",
                },
              ]}
            >
              <Input placeholder="Enter PIN code" maxLength={6} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="gstRate"
          label="GST Rate (%)"
          rules={[
            { required: true, message: "GST rate is required" },
            {
              type: "number",
              min: 0,
              max: 100,
              message: "GST rate must be between 0 and 100",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            type="number"
            placeholder="Enter GST rate"
            min={0}
            max={100}
          />
        </Form.Item>

        <Form.Item
          name="serviceChargeRate"
          label="Service Charge Rate (%)"
          rules={[
            { required: true, message: "Service charge rate is required" },
            {
              type: "number",
              min: 0,
              max: 100,
              message: "Service charge rate must be between 0 and 100",
            },
          ]}
        >
          <InputNumber
            style={{ width: "100%" }}
            placeholder="Enter service charge rate"
            type="number"
            min={0}
            max={100}
          />
        </Form.Item>
      </div>

      <Form.Item style={{ margin: 0 }}>
        <Button
          type="primary"
          htmlType="submit"
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </Form.Item>
    </Form>
  );
}
